import { Button } from "@/components/ui/button";
import { ArrowRight, Star } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback } from "react";
import heroBg from "@/assets/hero-bg.jpg";

const HeroSection = () => {
  const handleSmoothScroll = useCallback((href: string) => {
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  // Client avatars (using placeholder images)
  const clientAvatars = [
    "https://cdn.builder.io/api/v1/image/assets%2F06174d2cf2d84858a1dd8946a4942fa3%2F15efef937fee4d0cb38450035baa2e90?format=webp&width=800",
    "https://cdn.builder.io/api/v1/image/assets%2F06174d2cf2d84858a1dd8946a4942fa3%2Fda2558504f344b44a605114b913270fe?format=webp&width=800",
    "https://cdn.builder.io/api/v1/image/assets%2F06174d2cf2d84858a1dd8946a4942fa3%2F3f697e9950e64f5b819d78f3f6b05f6c?format=webp&width=800",
    "https://cdn.builder.io/api/v1/image/assets%2F06174d2cf2d84858a1dd8946a4942fa3%2F9607fa2df9a947c2b02a3175224ef632?format=webp&width=800",
    "https://cdn.builder.io/api/v1/image/assets%2F06174d2cf2d84858a1dd8946a4942fa3%2Fd356aa983dde49849b28ddc5e5fde8bb?format=webp&width=800",
  ];

  // Smooth animation transition settings
  const smoothTransition = { duration: 0.6, ease: "easeOut" as const };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image - Optimized with preload */}
      <motion.div
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.7 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        className="absolute inset-0 bg-cover bg-center bg-no-repeat will-change-transform"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed' // Parallax optimization
        }}
        role="img"
        aria-label="Brand design hero background"
      />
      
      {/* Blue Color Overlay */}
      <div className="absolute inset-0 bg-primary/50 mix-blend-overlay" />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/70 to-background" />
      
      {/* Blue Glow Effect */}
      <motion.div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[400px] md:w-[600px] h-[300px] sm:h-[400px] md:h-[600px] bg-primary/20 rounded-full blur-[100px] md:blur-[150px]"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.2 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
      />

      <div className="container mx-auto px-6 sm:px-8 relative z-10 pt-16 sm:pt-20 md:pt-24">
        <div className="max-w-4xl mx-auto text-center">
          {/* Trusted By Badge */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...smoothTransition, delay: 0.2 }}
            className="inline-flex flex-col items-center gap-2 mb-6 sm:mb-8"
          >
            <div className="flex items-center gap-3">
              {/* Client Avatars */}
              <div className="flex -space-x-3">
                {clientAvatars.map((avatar, index) => (
                  <motion.img
                    key={index}
                    src={avatar}
                    alt={`Client ${index + 1}`}
                    loading="eager"
                    decoding="async"
                    width={40}
                    height={40}
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 border-background object-cover"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...smoothTransition, delay: 0.3 + index * 0.1 }}
                  />
                ))}
              </div>
              
              {/* Star Rating */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...smoothTransition, delay: 0.5 }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary/50 border border-primary/20"
              >
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary fill-primary" />
                ))}
                <span className="text-xs sm:text-sm font-semibold text-foreground ml-1">5.0</span>
              </motion.div>
            </div>
            
            <span className="text-xs sm:text-sm font-medium text-muted-foreground">Trusted by Founders & CEOs</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...smoothTransition, delay: 0.3 }}
            className="font-bricolage text-4xl sm:text-5xl md:text-7xl lg:text-[90px] font-bold leading-[1.1] mb-4 sm:mb-6"
          >
            Brand Signatures
            <br />
            <motion.span 
              className="text-gradient-blue inline-block"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...smoothTransition, delay: 0.4 }}
            >
              That Sell
            </motion.span>
          </motion.h1>

          {/* Sub-headline */}
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...smoothTransition, delay: 0.5 }}
            className="text-[18px] text-muted-foreground max-w-2xl mx-auto mb-8 sm:mb-10 font-body leading-relaxed px-2"
          >
            I design brand identities for <span className="text-foreground font-semibold">Founders & CEOs</span> that 
            <span className="text-primary font-semibold"> remove buyer hesitation</span> and convert visitors into clients.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...smoothTransition, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4 sm:px-0 mb-16 sm:mb-20 md:mb-24"
          >
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <a href="https://forms.gle/5romGYrTtiPbxcxf6" target="_blank" rel="noopener noreferrer">
                <Button variant="hero" size="lg" className="group w-full sm:w-auto">
                  Start Your Project
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant="heroOutline"
                size="lg"
                className="w-full sm:w-auto"
                onClick={() => handleSmoothScroll("#portfolio")}
              >
                View Portfolio
              </Button>
            </motion.div>
          </motion.div>

        </div>
      </div>

    </section>
  );
};

export default HeroSection;
